import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, AlertTriangle, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteAccountDialog({ open, onOpenChange }: DeleteAccountDialogProps) {
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSubscriptionWarning, setShowSubscriptionWarning] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const navigate = useNavigate();

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();

    if (confirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }

    setIsDeleting(true);

    try {
      const { data, error } = await supabase.functions.invoke("delete-account");

      if (error) throw error;

      // Check for ACTIVE_SUBSCRIPTION in data response
      if (data?.error === "ACTIVE_SUBSCRIPTION") {
        setShowSubscriptionWarning(true);
        setIsDeleting(false);
        return;
      }

      if (data?.error) throw new Error(data.error);

      // Sign out and redirect
      await supabase.auth.signOut();
      toast.success("Your account has been deleted successfully");
      navigate("/");
    } catch (error: unknown) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to delete account");
      setIsDeleting(false);
    }
  };

  const handleOpenCustomerPortal = async () => {
    setIsLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        window.open(data.url, "_blank");
        toast.info("After canceling your subscription, return here to delete your account.");
      }
    } catch (error: unknown) {
      console.error(error);
      toast.error("Could not open subscription management. Please try again.");
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleBack = () => {
    setShowSubscriptionWarning(false);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setShowSubscriptionWarning(false);
      setConfirmText("");
    }
    onOpenChange(open);
  };

  // Show subscription warning view
  if (showSubscriptionWarning) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <CreditCard className="h-5 w-5" />
              Active subscription detected
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>
                You have an active paid subscription. To delete your account, you must first cancel
                your subscription.
              </p>
              <p>
                Click the button below to open the Stripe Customer Portal where you can manage and
                cancel your subscription.
              </p>
              <p className="text-sm text-muted-foreground">
                After canceling, return here to complete the account deletion.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 pt-4 sm:flex-row">
            <Button type="button" variant="outline" onClick={handleBack} disabled={isLoadingPortal}>
              Back
            </Button>
            <Button
              type="button"
              onClick={handleOpenCustomerPortal}
              disabled={isLoadingPortal}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isLoadingPortal && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Manage subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Default delete confirmation view
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleDelete}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete your account
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <p>
                This will permanently delete your productions, briefings, crew assignments and
                account data. <strong>This cannot be undone.</strong>
              </p>
              <p>
                If you still have an active paid subscription, you must cancel it first in the
                Billing section.
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="confirm-delete">
                Type <strong>DELETE</strong> to confirm
              </Label>
              <Input
                id="confirm-delete"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                disabled={isDeleting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={isDeleting || confirmText !== "DELETE"}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete account
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
